import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import { Fragment } from "react";
import { SlRefresh } from "react-icons/sl";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { searchByProp } from "utils/search";
import {
  CREATE_STOCK,
  FETCH_CHINA_STOCKS,
  FETCH_CHINA_STOCK_OVERVIEW,
} from "./graphql";

function ChinaStocks() {
  const { loading, error, data } = useQuery(FETCH_CHINA_STOCKS);
  const [createStock, createStockRes] = useMutation(CREATE_STOCK, {
    refetchQueries: [{ query: FETCH_CHINA_STOCKS }],
    onCompleted(res) {
      Alert("success", "Stock is created");
      navigate(`${res.createStock.stock.id}`);
    },
    onError(error) {
      Alert("error", parseError(error));
    },
  });
  const [fetchChinaStockOverview, fetchChinaStockOverviewRes] = useLazyQuery(
    FETCH_CHINA_STOCK_OVERVIEW,
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const params = Object.fromEntries(searchParams.entries());
  const q = searchParams.get("q") || "";

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  let filteredData = data.odooStocks
    .map((i) => {
      const combined =
        i.name + " - " + i.products.map((i) => i.number).join(", ");
      return { ...i, combined };
    })
    .filter((i) => searchByProp(i, ["combined"], q));

  function tryAddProductToStock() {
    const name = window.prompt("Input the name of the stock category:");
    if (!name) return;
    createStock({ variables: { name } });
  }

  let chinaStockOverview = fetchChinaStockOverviewRes.data?.chinaStockOverview;
  if (chinaStockOverview) {
    chinaStockOverview = JSON.parse(chinaStockOverview);

    filteredData = filteredData.map((i) => ({
      ...i,
      products: i.products.map((j) => ({
        ...j,
        odooAB: chinaStockOverview[`${j.odooId} ODOO AB`],
        odooLTD: chinaStockOverview[`${j.odooId} ODOO LTD`],
        ssFc: chinaStockOverview[`${j.odooId} SS`],
        fc: chinaStockOverview[`${j.odooId} FC`],
      })),
    }));
  }

  const rowStyle = (product) => {
    if (
      product.odooAB < 0 ||
      product.odooLTD < 0 ||
      product.ssFc < 0 ||
      product.fc < 0
    )
      return "bg-red-100 text-red-600";
    return "";
  };

  return (
    <div className="p-6 flex-1 overflow-auto">
      <div className="card p-4 h-full overflow-auto flex flex-col">
        <div className="flex justify-between place-items-center space-x-6">
          <SearchBar
            autoFocus
            containerClassName="flex-1"
            placeholder="Search by name"
            initialQuery={q}
            onChange={(text) => setSearchParams({ ...params, q: text })}
          />
          <Button
            onClick={() => {
              fetchChinaStockOverview({
                variables: {
                  productOdooIds: filteredData.flatMap((i) =>
                    i.products.map((j) => j.odooId),
                  ),
                },
                fetchPolicy: "network-only",
                onError(res) {
                  Alert("error", parseError(res));
                },
              });
            }}
            loading={fetchChinaStockOverviewRes.loading}
            icon={<SlRefresh />}
          >
            Load All Forecasted
          </Button>
          <Button
            onClick={tryAddProductToStock}
            loading={createStockRes.loading}
          >
            + Add New Stock
          </Button>
        </div>
        <div className="mt-4 -mx-4 flex-1 overflow-auto">
          <table>
            <tbody className="divide-y dark:divide-gray-700">
              {filteredData.map((stock, stockIndex) => (
                <Fragment key={stockIndex}>
                  <tr className="bg-indigo-100 dark:bg-gray-800 font-semibold sticky top-0 z-10">
                    <td className="px-6 whitespace-nowrap">
                      <Link to={`${stock.id}?q=${q}`}>{stock.name}</Link>
                    </td>
                    <td className="text-right opacity-70 px-6">Odoo AB</td>
                    <td className="text-right opacity-70 px-6">Odoo LTD</td>
                    <td className="text-right opacity-70 px-6">SS</td>
                    <td className="text-right opacity-70 px-6">FC</td>
                  </tr>
                  {stock.products.map((product, index) => (
                    <tr key={index} className={`z-10 ${rowStyle(product)}`}>
                      <td className="px-6">
                        <Link
                          to={`${stock.id}?productOdooId=${product.odooId}&q=${q}`}
                        >
                          {product.number}
                        </Link>
                        <span className="opacity-60 pl-2">
                          W#{product.id},OD#{product.odooId}
                        </span>
                      </td>
                      <td className="text-right px-6">{product.odooAB}</td>
                      <td className="text-right px-6">{product.odooLTD}</td>
                      <td className="text-right px-6">{product.ssFc}</td>
                      <td className="text-right px-6">{product.fc}</td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ChinaStocks;
